import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n";

interface UserProfile {
  name: string;
  avatarUrl: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  preferredLanguage: string | null;
}

const DEFAULT_PROFILE: UserProfile = {
  name: "",
  avatarUrl: null,
  country: null,
  state: null,
  city: null,
  preferredLanguage: null,
};

async function fetchUserProfile(userId: string): Promise<UserProfile> {
  const { data, error } = await supabase
    .from("profiles")
    .select("name, avatar_url, country, state, city, preferred_language")
    .eq("user_id", userId)
    .single();

  if (error) throw error;

  const lang = data.preferred_language || null;
  if (lang && lang !== i18n.language?.substring(0, 2)) {
    i18n.changeLanguage(lang);
  }

  return {
    name: data.name || "",
    avatarUrl: data.avatar_url || null,
    country: data.country || null,
    state: data.state || null,
    city: data.city || null,
    preferredLanguage: lang,
  };
}

export function useUserProfile() {
  const { user } = useAuth();

  const { data: profile = DEFAULT_PROFILE, isLoading: loading } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: () => fetchUserProfile(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...profile,
    loading,
    firstName: profile.name.split(" ")[0] || "",
  };
}
